import React, { useState, useCallback, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import LazyImage from '../shared/LazyImage'
import { getQuizOptionsData } from '../../constants'
import { loadCharacterAttributes } from '../../utils/api'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStep4 } from '../../utils/analytics'

const Step3 = ({ onAnswer, onPrevious, selections }) => {
  const [selectedHairColor, setSelectedHairColor] = useState('')
  const [selectedHairStyle, setSelectedHairStyle] = useState('')
  const [selectedEyeColor, setSelectedEyeColor] = useState('')
  const [attributesMap, setAttributesMap] = useState({})
  const location = useLocation()

  const selectedStyle = selections?.welcome || 'Realistic'
  const isHalloween = !!location.pathname.includes('/halloween')

  useEffect(() => {
    ;(async () => {
      const map = await loadCharacterAttributes()
      setAttributesMap(map)
    })()
  }, [])

  const hairColors = getQuizOptionsData('hairColor', selectedStyle)
  const hairStyles = getQuizOptionsData('hairStyle', selectedStyle)
  const eyeColors = getQuizOptionsData('eyeColor', selectedStyle)

  // Auto-select first options on mount
  useEffect(() => {
    if (hairColors && hairColors.length > 0 && !selectedHairColor) {
      setSelectedHairColor(hairColors[0].value)
    }
  }, [hairColors])

  useEffect(() => {
    if (hairStyles && hairStyles.length > 0 && !selectedHairStyle) {
      setSelectedHairStyle(hairStyles[0].value)
    }
  }, [hairStyles])

  useEffect(() => {
    if (eyeColors && eyeColors.length > 0 && !selectedEyeColor) {
      setSelectedEyeColor(eyeColors[0].value)
    }
  }, [eyeColors])

  const getAttributeId = (type, label) => {
    if (!attributesMap[type] || !label) return null
    return attributesMap[type][label.trim()] || null
  }

  const handleHairColorClick = useCallback(
    value => {
      setSelectedHairColor(value)
      const id = getAttributeId('haircolor', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap],
  )

  const handleHairStyleClick = useCallback(
    value => {
      setSelectedHairStyle(value)
      const id = getAttributeId('hairstyle', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap],
  )

  const handleEyeColorClick = useCallback(
    value => {
      setSelectedEyeColor(value)
      const id = getAttributeId('eyecolor', value)
      console.log('📝 eyecolor answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap],
  )

  const handleContinue = useCallback(() => {
    console.log('Continue clicked with:', {
      hairColor: selectedHairColor,
      hairStyle: selectedHairStyle,
      eyeColor: selectedEyeColor,
    })

    trackStep4(selectedHairStyle, selectedHairColor, selectedEyeColor)

    onAnswer('step3', {
      hairColor: selectedHairColor,
      hairStyle: selectedHairStyle,
      eyeColor: selectedEyeColor,
    })
  }, [selectedHairColor, selectedHairStyle, selectedEyeColor, onAnswer])

  // Allow continue if all selections are made
  const canContinue = selectedHairColor && selectedHairStyle && selectedEyeColor
  const { t } = useTranslation()

  return (
    <div className='quizContentWrapper'>
      <QuizContent className='step3-design'>
        {/* Hair Style Block */}
        <div className='step3-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('hairS_text')}</span>
              </>
            ) : (
              t('choose_h_style')
            )}
          </QuizTitle>
          <QuizOptions className='hairstyle-options'>
            {hairStyles.map(style => (
              <QuizOption
                key={style.value}
                value={style.value}
                name='hairStyle'
                onClick={handleHairStyleClick}
                className={`hairstyle-option ${selectedHairStyle === style.value ? 'selected' : selectedHairStyle ? 'not-selected' : ''} ${
                  hairStyles.length % 2 === 0 ? 'even' : 'odd'
                }`}
              >
                <div className='hairstyle-image'>
                  <LazyImage src={style.image} alt={style.alt} />
                </div>
                <div className='image-title'>{t(style.text)}</div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        {/* Hair Color Block */}
        <div className='step3-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('hairC_text')}</span>
              </>
            ) : (
              t('choose_h_color')
            )}
          </QuizTitle>
          <QuizOptions className='hair-color-options'>
            {hairColors.map(color => (
              <QuizOption
                key={color.value}
                value={color.value}
                name='hairColor'
                onClick={handleHairColorClick}
                className={`hair-color-option ${selectedHairColor === color.value ? 'selected' : selectedHairColor ? 'not-selected' : ''} ${
                  hairColors.length % 2 === 0 ? 'even' : 'odd'
                }`}
              >
                <div className='hair-color-image'>
                  <LazyImage src={color.image} alt={color.alt} />
                </div>
                <div className='image-title'>{t(color.text)}</div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        {/* Eye Color Block */}
        <div className='step3-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('eye_text')}</span>
              </>
            ) : (
              t('choose_eye')
            )}
          </QuizTitle>
          <QuizOptions className='eye-color-options'>
            {eyeColors.map(color => (
              <QuizOption
                key={color.value}
                value={color.value}
                name='eyeColor'
                onClick={handleEyeColorClick}
                className={`eye-color-option ${selectedEyeColor === color.value ? 'selected' : selectedEyeColor ? 'not-selected' : ''} ${
                  eyeColors.length % 2 === 0 ? 'even' : 'odd'
                }`}
              >
                <div className='eye-color-image'>
                  <LazyImage src={color.image} alt={color.alt} />
                </div>
                <div className='image-title'>{t(color.text)}</div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        <div className='navigation-wrapper'>
          <button className='previous-button' onClick={onPrevious}>
            {t('previous')}
          </button>
          <button className='continue-button' onClick={handleContinue} disabled={!canContinue}>
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step3
